'use client';

import React, { useEffect, useRef, useState } from 'react';
import { getConsent, saveConsent, detectRegion } from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  onClose: () => void;
  onSave?: () => void;
}

export default function ConsentPreferenceCenter({
  onClose,
  onSave,
}: ConsentPreferenceCenterProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [choices, setChoices] = useState({
    analytics: false,
    marketing: false,
    preferences: false,
  });

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      setChoices(existing.choices);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
  }, []);

  const handleSave = () => {
    const region = detectRegion();
    saveConsent(choices, region);
    onSave?.();
    onClose();
  };

  const handleAcceptAll = () => {
    const region = detectRegion();
    saveConsent(
      {
        analytics: true,
        marketing: true,
        preferences: true,
      },
      region
    );
    onSave?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}
        tabIndex={-1}
      >
        <div className="p-6">
          <h2 id="preferences-title" className="text-xl font-semibold mb-4">
            Cookie-Einstellungen
          </h2>

          <div className="space-y-4 mb-6">
            {/* Necessary - Always ON */}
            <div className="p-3 rounded border" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Notwendig</h3>
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    Immer erforderlich für die Website-Funktionalität
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked
                  disabled
                  className="w-10 h-6 rounded-full bg-green-500 transition-colors cursor-not-allowed"
                >
                  <span
                    className="block w-4 h-4 bg-white rounded-full shadow transition-transform translate-x-5"
                  />
                </button>
              </div>
            </div>

            {/* Analytics */}
            <div className="p-3 rounded border" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Analytik</h3>
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    Hilft uns, die Website zu verbessern
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.analytics}
                  onClick={() =>
                    setChoices((prev) => ({
                      ...prev,
                      analytics: !prev.analytics,
                    }))
                  }
                  className={`w-10 h-6 rounded-full transition-colors ${
                    choices.analytics ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      choices.analytics ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Marketing */}
            <div className="p-3 rounded border" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Marketing</h3>
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    Für personalisierte Werbung
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.marketing}
                  onClick={() =>
                    setChoices((prev) => ({
                      ...prev,
                      marketing: !prev.marketing,
                    }))
                  }
                  className={`w-10 h-6 rounded-full transition-colors ${
                    choices.marketing ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      choices.marketing ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Preferences */}
            <div className="p-3 rounded border" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Präferenzen</h3>
                  <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    Für personalisierte Inhalte
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.preferences}
                  onClick={() =>
                    setChoices((prev) => ({
                      ...prev,
                      preferences: !prev.preferences,
                    }))
                  }
                  className={`w-10 h-6 rounded-full transition-colors ${
                    choices.preferences ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      choices.preferences ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={handleSave}
              className="w-full px-4 py-2 rounded font-medium text-sm transition-colors"
              style={{
                backgroundColor: 'var(--accent)',
                color: 'white',
              }}
            >
              Einstellungen speichern
            </button>
            <button
              onClick={handleAcceptAll}
              className="w-full px-4 py-2 rounded font-medium text-sm transition-colors border"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--foreground)',
              }}
            >
              Alle akzeptieren
            </button>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 rounded font-medium text-sm transition-colors"
              style={{
                color: 'var(--muted-foreground)',
              }}
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
